import { IMovementRS, TMovementElementItem } from '../models/salesModel'
import { roundValue } from './index'

export type TSummary = {
  totalSales: number
  totalAmount: number
  totalTaxes: number
  totalDiscounts: number
  averagePerSale: number
  totalItems: number
}

export type TGroupedData = {
  key: string
  label: string
  count: number
  totalAmount: number
  totalTaxes: number
  totalDiscounts: number
  percentage: number
}

export type TProductRanking = {
  key: string
  itemCode: string
  itemName: string
  totalQuantity: number
  totalAmount: number
  totalTaxes: number
  totalDiscounts: number
  averagePrice: number
  transactionCount: number
}

/**
 * Calcula el resumen general de un conjunto de movimientos de venta
 */
export function calculateSummary(movements: IMovementRS[]): TSummary {
  const totalSales = movements.length
  const totalAmount = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount || 0), 0)
  const totalTaxes = movements.reduce((acc, m) => acc + (m.total_tax_amount || 0), 0)
  const totalDiscounts = movements.reduce((acc, m) => acc + (m.total_discount_amount || 0), 0)
  const totalItems = movements.reduce((acc, m) => acc + (m.items || m.item_list?.length || 0), 0)
  const averagePerSale = totalSales > 0 ? roundValue(totalAmount / totalSales) : 0

  return {
    totalSales,
    totalAmount: roundValue(totalAmount),
    totalTaxes: roundValue(totalTaxes),
    totalDiscounts: roundValue(totalDiscounts),
    averagePerSale,
    totalItems
  }
}

/**
 * Agrupa ventas por vendedor (created_by_user_name)
 */
export function groupSalesBySeller(movements: IMovementRS[]): TGroupedData[] {
  const totalGeneral = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount || 0), 0)
  const map = new Map<string, Omit<TGroupedData, 'percentage'>>()

  movements.forEach(m => {
    const key = m.created_by_user_name || 'Sin vendedor'
    const existing = map.get(key)
    const amount = m.total_amount_net || m.total_amount || 0
    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label: key,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0
      })
    }
  })

  return Array.from(map.values())
    .map(g => ({
      ...g,
      totalAmount: roundValue(g.totalAmount),
      totalTaxes: roundValue(g.totalTaxes),
      totalDiscounts: roundValue(g.totalDiscounts),
      percentage: totalGeneral > 0 ? roundValue((g.totalAmount / totalGeneral) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

/**
 * Agrupa ventas por cliente (contact_name)
 */
export function groupSalesByCustomer(movements: IMovementRS[]): TGroupedData[] {
  const totalGeneral = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount || 0), 0)
  const map = new Map<string, Omit<TGroupedData, 'percentage'>>()

  movements.forEach(m => {
    const key = m.contact_name || 'Consumidor Final'
    const existing = map.get(key)
    const amount = m.total_amount_net || m.total_amount || 0
    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label: key,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0
      })
    }
  })

  return Array.from(map.values())
    .map(g => ({
      ...g,
      totalAmount: roundValue(g.totalAmount),
      totalTaxes: roundValue(g.totalTaxes),
      totalDiscounts: roundValue(g.totalDiscounts),
      percentage: totalGeneral > 0 ? roundValue((g.totalAmount / totalGeneral) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

/**
 * Obtiene la etiqueta legible del método de pago de un movimiento.
 * Revisa m.payments y m.payment_term con fallback a paymentOptions o diccionario estándar.
 */
export function getMovementPaymentLabel(m: IMovementRS, paymentOptions?: any[]): string {
  if (m.payments && m.payments.length > 0) {
    const methods = m.payments
      .map(p => p.payment_method)
      .filter(Boolean)
      .map(pm => {
        const found = paymentOptions?.find((opt: any) => opt.key === pm)
        if (found?.string_value) return found.string_value
        if (pm === 'CASH') return 'Contado'
        if (pm === 'CREDIT') return 'Crédito'
        if (pm === 'TRANSFER') return 'Transferencia'
        if (pm === 'CARD') return 'Tarjeta'
        return pm || ''
      })
      .filter(Boolean)

    const uniqueMethods = Array.from(new Set(methods))
    if (uniqueMethods.length > 0) {
      return uniqueMethods.join(', ')
    }
  }

  if (m.payment_term === 'CREDIT') {
    return 'Crédito'
  }

  return 'Contado'
}

/**
 * Agrupa ventas por método de pago
 */
export function groupSalesByPaymentMethod(movements: IMovementRS[], paymentOptions?: any[]): TGroupedData[] {
  const totalGeneral = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount || 0), 0)
  const map = new Map<string, Omit<TGroupedData, 'percentage'>>()

  movements.forEach(m => {
    if (m.payments && m.payments.length > 0) {
      m.payments.forEach(p => {
        const pmKey = p.payment_method || ''
        const foundOpt = paymentOptions?.find((opt: any) => opt.key === pmKey)
        let key = foundOpt?.string_value || pmKey
        if (key === 'CASH') key = 'Contado'
        if (key === 'CREDIT') key = 'Crédito'
        if (key === 'TRANSFER') key = 'Transferencia'
        if (key === 'CARD') key = 'Tarjeta'
        if (!key) key = m.payment_term === 'CREDIT' ? 'Crédito' : 'Contado'

        const existing = map.get(key)
        const amount = p.amount || m.total_amount_net || m.total_amount || 0
        if (existing) {
          existing.count++
          existing.totalAmount += amount
        } else {
          map.set(key, {
            key,
            label: key,
            count: 1,
            totalAmount: amount,
            totalTaxes: 0,
            totalDiscounts: 0
          })
        }
      })
    } else {
      const key = getMovementPaymentLabel(m, paymentOptions)
      const existing = map.get(key)
      const amount = m.total_amount_net || m.total_amount || 0
      if (existing) {
        existing.count++
        existing.totalAmount += amount
      } else {
        map.set(key, {
          key,
          label: key,
          count: 1,
          totalAmount: amount,
          totalTaxes: 0,
          totalDiscounts: 0
        })
      }
    }
  })

  return Array.from(map.values())
    .map(g => ({
      ...g,
      totalAmount: roundValue(g.totalAmount),
      percentage: totalGeneral > 0 ? roundValue((g.totalAmount / totalGeneral) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}

/**
 * Obtiene ranking de productos más vendidos
 */
export function getTopProducts(movements: IMovementRS[], limit: number = 50): TProductRanking[] {
  const map = new Map<string, TProductRanking>()

  movements.forEach(m => {
    if (m.item_list && Array.isArray(m.item_list)) {
      m.item_list.forEach((item: TMovementElementItem) => {
        const key = item.uid || item.key || item.item_code || item.custom_id || item.item_name || item.name
        if (!key) return

        const existing = map.get(key)
        const qty = Number(item.quantity) || 0
        const unitPrice = Number(item.unit_price) || 0
        const calculatedAmount = qty * unitPrice
        const amount = item.total_amount_net || item.total_amount_full || item.total_amount || calculatedAmount || 0

        if (existing) {
          existing.totalQuantity += qty
          existing.totalAmount += amount
          existing.totalTaxes += item.total_tax_amount || 0
          existing.totalDiscounts += item.discount_amount || 0
          existing.transactionCount++
        } else {
          map.set(key, {
            key,
            itemCode: item.item_code || item.custom_id || item.key || '',
            itemName: item.item_name || item.name || 'Producto sin nombre',
            totalQuantity: qty,
            totalAmount: amount,
            totalTaxes: item.total_tax_amount || 0,
            totalDiscounts: item.discount_amount || 0,
            averagePrice: 0,
            transactionCount: 1
          })
        }
      })
    }
  })

  return Array.from(map.values())
    .map(p => ({
      ...p,
      totalAmount: roundValue(p.totalAmount),
      totalTaxes: roundValue(p.totalTaxes),
      totalDiscounts: roundValue(p.totalDiscounts),
      averagePrice: p.totalQuantity > 0 ? roundValue(p.totalAmount / p.totalQuantity) : 0
    }))
    .sort((a, b) => b.totalQuantity - a.totalQuantity)
    .slice(0, limit)
}

/**
 * Agrupa ventas por punto de venta (pos_name)
 */
export function groupSalesByPos(movements: IMovementRS[]): TGroupedData[] {
  const totalGeneral = movements.reduce((acc, m) => acc + (m.total_amount_net || m.total_amount || 0), 0)
  const map = new Map<string, Omit<TGroupedData, 'percentage'>>()

  movements.forEach(m => {
    const key = m.pos_name || 'Sin punto de venta'
    const existing = map.get(key)
    const amount = m.total_amount_net || m.total_amount || 0
    if (existing) {
      existing.count++
      existing.totalAmount += amount
      existing.totalTaxes += m.total_tax_amount || 0
      existing.totalDiscounts += m.total_discount_amount || 0
    } else {
      map.set(key, {
        key,
        label: key,
        count: 1,
        totalAmount: amount,
        totalTaxes: m.total_tax_amount || 0,
        totalDiscounts: m.total_discount_amount || 0
      })
    }
  })

  return Array.from(map.values())
    .map(g => ({
      ...g,
      totalAmount: roundValue(g.totalAmount),
      totalTaxes: roundValue(g.totalTaxes),
      totalDiscounts: roundValue(g.totalDiscounts),
      percentage: totalGeneral > 0 ? roundValue((g.totalAmount / totalGeneral) * 100) : 0
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
}
